import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser, ROLES, PERMISSIONS } from '../../contexts/UserContext';

const ProtectedRoute = ({ 
  children, 
  roles = [], 
  permissions = [], 
  requireAll = false,
  redirectTo = '/login',
  fallback = null 
}) => {
  const { 
    currentUser, 
    loading, 
    isAuthenticated,
    hasAnyRole,
    hasAnyPermission, 
    hasAllPermissions 
  } = useUser();
  
  const location = useLocation();

  // Show loading state
  if (loading) {
    return fallback || (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated || !currentUser) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check role-based access
  if (roles.length > 0) {
    const hasRequiredRole = hasAnyRole(roles);
    
    if (!hasRequiredRole) {
      // Redirect to appropriate dashboard based on user's actual role
      const roleRedirects = {
        'SUPER_ADMIN': '/super-admin-dashboard',
        'ADMIN': '/admin-dashboard',
        'MANAGER': '/manager-dashboard',
        'FINANCE_MANAGER': '/finance-dashboard',
        'CONTENT_MANAGER': '/content-moderation',
        'AGGREGATOR': '/aggregator-dashboard',
        'CHANNEL_OWNER': '/channel-dashboard',
        'CREATOR': '/creator-dashboard',
        'CONTENT_CREATOR': '/creator-dashboard',
        'STUDIO': '/studio-dashboard',
        'INVESTOR': '/investor-dashboard',
        'PREMIUM_INVESTOR': '/investor-dashboard',
        'SUPPORT_AGENT': '/support-dashboard',
        'ANALYTICS_VIEWER': '/analytics-dashboard',
        'USER': '/dashboard'
      };

      const userDashboard = roleRedirects[currentUser.role] || '/dashboard';
      return <Navigate to={userDashboard} replace />;
    }
  }

  // Check permission-based access
  if (permissions.length > 0) {
    const hasRequiredPermissions = requireAll 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);
    
    if (!hasRequiredPermissions) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // User has access, render children
  return children;
};

// Specialized Protected Routes for common use cases

export const AdminRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const ManagerRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const ContentModeratorRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.CONTENT_MANAGER]} 
    permissions={[PERMISSIONS.APPROVE_CONTENT, PERMISSIONS.REJECT_CONTENT]}
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const FinanceRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.FINANCE_MANAGER]} 
    permissions={[PERMISSIONS.VIEW_FINANCE]}
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const AggregatorRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.AGGREGATOR]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const ChannelOwnerRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.CHANNEL_OWNER]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const CreatorRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.CREATOR, ROLES.CONTENT_CREATOR]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const InvestorRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.INVESTOR, ROLES.PREMIUM_INVESTOR]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const StudioRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.STUDIO]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const SupportRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.SUPPORT_AGENT]} 
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export const AnalyticsRoute = ({ children, fallback }) => (
  <ProtectedRoute 
    roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.MANAGER, ROLES.ANALYTICS_VIEWER]} 
    permissions={[PERMISSIONS.VIEW_ANALYTICS]}
    fallback={fallback}
  >
    {children}
  </ProtectedRoute>
);

export default ProtectedRoute;
